import {Style, Circle as CircleStyle, Fill, Stroke} from 'ol/style';
import {ScaleLine, defaults as defaultControls} from 'ol/control';
import {useState, useEffect, useRef} from 'react';
import {fromLonLat, transform} from 'ol/proj';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import TileLayer from 'ol/layer/Tile';
import {observer} from 'mobx-react';
import Overlay from 'ol/Overlay';
import Point from 'ol/geom/Point';
import Feature from 'ol/Feature';
import OSM from 'ol/source/OSM';
import View from 'ol/View';
import Map from 'ol/Map';
import 'ol/ol.css';
import {useGlobalStorage} from '../mobx/GlobalStorage.js';
import {TAB_LOCATION, TAB_CITY} from './StatsCommon.js';
import {MapObsPopup} from '../MapStyles.js';
import {COUNTRY_ID_FI} from '../Util.js';
import {getCityName} from '../Trans.js';
import {CT_MAP} from './StatsCity.js';
import {COMM_OK} from '../WsR2.js';

const StatsCityMap = observer((props) => {
	const [clickFt, setClickFt] = useState({pos:0, fa:[]});
	const [resizeDone, setResizeDone] = useState(false);
	const gStorage = useGlobalStorage();
	const mapMapRef = useRef(null);
	const mapPopRef = useRef(null);

	useEffect(() => {
		const initialObsLayer = new VectorLayer({source: new VectorSource()});

		const initialMap = new Map({
			controls: defaultControls({rotate: false, zoom: false}).extend([new ScaleLine({units: 'metric'})]),

			layers: [
				new TileLayer({
					source: new OSM(),
				}),
				initialObsLayer,
			],

			/* Zoomaa Suomen kokoiseksi */
			view: new View({
					center: fromLonLat([27.138596, 65.441896]),
					zoom: 5,
				}),
			moveTolerance: 8
		});

		/* ---------------------------------------------------------- */

		initialMap.on('click', mapClick);

		gStorage.map.setMap(initialMap, initialObsLayer);

		/* ---------------------------------------------------------- */

		const h = window.innerHeight - 
			((document.getElementById('appbar') ? document.getElementById('appbar').clientHeight : 48) +
			 (document.getElementById('statstab') ? document.getElementById('statstab').clientHeight : 48) + 
			 (document.getElementById('locationtab') ? document.getElementById('locationtab').clientHeight : 48) +
			 (document.getElementById('cityfilter') ? document.getElementById('cityfilter').clientHeight : 88) + 
			 (document.getElementById('citytab') ? document.getElementById('citytab').clientHeight : 48) + 16);

		gStorage.map.setMapSize(window.innerWidth, h);

		/* ---------------------------------------------------------- */

		window.addEventListener('resize', handleResize);

		return (() => {
			window.removeEventListener('resize', handleResize);
		});
		// eslint-disable-next-line
	}, []);

	useEffect(() => {
		// console.log('Map: useEffect, currents', mapMapRef.current, mapPopRef.current);

		if (mapMapRef.current) {
			gStorage.map.map.setTarget(mapMapRef.current);

			if (mapPopRef.current) {
				while (gStorage.map.map.getOverlays().getLength() > 0) {
					gStorage.map.map.removeOverlay(gStorage.map.map.getOverlays().getArray()[0]);
				}

				gStorage.map.map.addOverlay(new Overlay({element: mapPopRef.current, id: 'city'}));
			}
		}
		// eslint-disable-next-line
	}, [mapMapRef.current, mapPopRef.current, gStorage.stats.tab, gStorage.stats.loc_tab, gStorage.stats.city_tab]);

	useEffect(() => {
		if (!gStorage.map.obsLayer || gStorage.stats.city_tab !== CT_MAP)
			return;

		if (gStorage.map.map.getOverlayById('city')) 
			gStorage.map.map.getOverlayById('city').setPosition(undefined);

		fillMap();

		updateViewport();
		// eslint-disable-next-line
	}, [gStorage.stats.obsList]);

	useEffect(() => {
		if (gStorage.stats.city_tab !== CT_MAP)
			return;

		if (resizeDone === false) {
			updateViewport();
		} else {
			setResizeDone(false);
		}
		// eslint-disable-next-line
	}, [resizeDone]);

	/* Update obs popup */
	useEffect(() => {
		if (!gStorage.map.map || !gStorage.map.map.getOverlayById('city')) {
// 			console.log('useEffect: clikcFt null map!')
			return;
		}

		gStorage.map.map.getOverlayById('city').setPosition(undefined);

		if (!clickFt.fa.length || clickFt.pos >= clickFt.fa.length)
			return;

		const obsCity = clickFt.fa[clickFt.pos].get('city');
		if (obsCity) {
			gStorage.map.map.getOverlayById('city').setPosition(clickFt.fa[clickFt.pos].coordinate);

			document.getElementById('obs-species').innerHTML = cityMapGetSpeciesName(obsCity.id) + ', ' + obsCity.n_spec;
		}
		// eslint-disable-next-line
	}, [clickFt]);

	function updateViewport() {
		if (gStorage.stats.obsList && gStorage.stats.obsList.length > 0) {
			gStorage.map.map.getView().fit(gStorage.map.obsLayer.getSource().getExtent(), {
				size: gStorage.map.map.getSize(),
				maxZoom: 16,
				padding: [32, 32, 32, 32]});
		} else {
			gStorage.map.map.setView(
				new View({
					center: fromLonLat([27.138596, 65.441896]),
					zoom: 5
				})
			);
		}
	}

	function handleResize() {
		/* Availabilty depends on rendering order => q&d hard coded defaults! */
		const h = window.innerHeight - 
			((document.getElementById('appbar') ? document.getElementById('appbar').clientHeight : 48) +
			 (document.getElementById('statstab') ? document.getElementById('statstab').clientHeight : 48) + 
			 (document.getElementById('locationtab') ? document.getElementById('locationtab').clientHeight : 48) +
			 (document.getElementById('cityfilter') ? document.getElementById('cityfilter').clientHeight : 88) + 
			 (document.getElementById('citytab') ? document.getElementById('citytab').clientHeight : 48) + 16);

		gStorage.map.setMapSize(window.innerWidth, h);
		setResizeDone(true);
	}

	function mapClick(e) {
		let ft = [];
		e.map.forEachFeatureAtPixel(e.pixel, (feat) => {
			feat.coordinate = e.coordinate;
			ft.push(feat);
		});

		ft.sort((a, b) => {
			return b.get('city').n_spec - a.get('city').n_spec;
		});

		setClickFt({pos: 0, fa: ft});
	}

	function getCityColor(city) {
		if (city.n_spec < 10)
			return 'red';
		if (city.n_spec < 30)
			return 'salmon';
		if (city.n_spec < 50)
			return '#f4a79d';
		if (city.n_spec < 75)
			return '#a8c568';
		if (city.n_spec < 100)
			return '#8ab457';
		if (city.n_spec < 125)
			return '#6f9c4a';
		if (city.n_spec < 150)
			return '#56853d';
		if (city.n_spec < 175)
			return '#3f6f29';
		if (city.n_spec < 200)
			return '#2b4c1e';
			
		return '#2b4c1e';
	}

	function fillMap() {
		let features = [];

		gStorage.stats.obsList && gStorage.stats.obsList.forEach((city, ind) => {
			let tgtFeat = new Feature({
				geometry: new Point(transform([city.lon / 1000000.0, city.lat / 1000000.0], 'EPSG:4326', 'EPSG:3857')),
				city: city
			});

			const style = new Style({
				image: new CircleStyle({
					radius: 12,
					fill: new Fill({color: getCityColor(city)}),
					stroke: new Stroke({color: '#ffffff', width: 1}),
				}),
			});

			tgtFeat.setStyle(style);
			features.push(tgtFeat);
		});

		if (features.length) {
			gStorage.map.obsLayer.setSource(
				new VectorSource({
					features: features,
				})
			)
		} else {
			gStorage.map.obsLayer.getSource().clear();
		}
	}

	function proceedDetails(dir) {
		setClickFt({pos: clickFt.pos + dir, fa: clickFt.fa});
	}

	function cityMapGetSpeciesName(city_id) {
		const city = gStorage.lists.citylist.list.find((city) => city.id === city_id);

		return getCityName(city, gStorage.conn.user);
	}

	function statsCityMapsRxWs(status, msg) {
		// setLoading(false);

		if (status !== COMM_OK) {
			alert('statCityMapsRxWs: error ' + status);
			return;
		} else if (msg.result !== 'OK') {
			alert('statCityMapsRxWs: ' + msg.data.desc + '!');
			return;
		}

		if (gStorage.stats.tab !== TAB_LOCATION || gStorage.stats.loc_tab !== TAB_CITY || gStorage.stats.city_tab !== CT_MAP)
			return;

		// console.log('statCityMapsRxWs', msg);
		gStorage.stats.setObsList(msg.data.list.filter((city) => city.n_spec > 0).toSorted((a, b) => a.n_spec - b.n_spec));
	}

	function statsCityMapLoadMapCount() {
		let msg = {
			sess_id: gStorage.conn.sess_id,
			cmd: 'location',
			op: 'citymapcount',
			data: {
				country: gStorage.stats.country.id,
				year: gStorage.stats.statsYear,
				user: gStorage.conn.user.id,
			}
		};

		// console.log('statsCityMapLoadMapCount', msg);
		gStorage.conn.ws.send(msg, statsCityMapsRxWs);
	}

	if (gStorage.stats.city_tab !== CT_MAP)
		return null;

	if (!gStorage.stats.obsList && gStorage.conn.online) {
		if (gStorage.stats.country.id === COUNTRY_ID_FI) {
			statsCityMapLoadMapCount();

			if (mapMapRef && mapMapRef.current)
				return null;
		} else {
			console.error('Map available for Finland only!');
		}
	}
	
	return (
		<div 
		    ref={mapMapRef}
		    id="mapContainer" 
		    style={{width: '100%', height: gStorage.map.mapHeight}}>
			<MapObsPopup map={gStorage.map.map} proceedDetails={proceedDetails} pos={clickFt.pos} num={clickFt.fa.length} mapPopRef={mapPopRef}/>
		</div>
	);
});

export default StatsCityMap;
